import { resolverPortalDeLaUrl } from '@/router/tenant-basename';
import { storage } from './storage';

/**
 * El slug de la empresa que dice la URL ahora mismo — la misma fuente que usa
 * el router para el `basename` — o `null` en una ruta pública.
 *
 * Vive acá y no en el store de auth porque el interceptor de Axios corre
 * AFUERA de React: no tiene un `portalActivo` que leer del store, sólo la URL
 * del navegador. Usar la misma función que el router evita que interceptor y
 * router lean dos empresas distintas de la misma URL.
 */
export function slugDeLaUrlActual(): string | null {
  const portal = resolverPortalDeLaUrl(window.location.pathname);
  return portal.tipo === 'empresa' ? portal.slug : null;
}

/**
 * Da vuelta una clave "global" (`auth_token`) en una por-empresa
 * (`auth_token:datec`).
 *
 * 🔴 Por qué existe. El token, el refresh token y la organización activa se
 * leen en CADA request desde el interceptor de Axios, fuera de React. Con una
 * sola clave por navegador, dos pestañas en dos empresas se pisan: la que
 * escribe último gana, y la otra pestaña sigue mostrando su empresa mientras
 * sus requests viajan con el token de la otra — lectura y escritura cruzadas
 * entre inquilinos, bajo una pantalla que dice lo contrario.
 *
 * Con `slug` nulo devuelve la clave de siempre: una ruta pública, o el
 * instante antes de elegir empresa, todavía necesitan una clave que leer.
 */
export function clavePorPortal(base: string, slug: string | null): string {
  return slug ? `${base}:${slug}` : base;
}

const ULTIMO_PORTAL_KEY = 'ultimo_portal_activo';

/**
 * Guarda cuál fue la última empresa con la que se abrió sesión de verdad
 * (`login` o el canje de portal). Se escribe donde se escribe la sesión
 * (`abrirSesion`, en el store) y se olvida al cerrar sesión.
 *
 * 🔴 Por qué existe. Todo el mundo tiene una membresía — la migración de
 * relleno le dio una a cada usuario existente — así que `auth_token` (la
 * clave global, sin empresa) no lo escribe nadie más. Antes de este puntero,
 * cualquier URL sin empresa (la landing, un bookmark viejo como `/quotes`)
 * hacía que `leerPorPortal` cayera a esa clave global vacía: cero token, cero
 * sesión, aunque `auth_token:datec` siguiera perfectamente vivo. Visitar la
 * landing te sacaba de la app.
 */
export function guardarUltimoPortal(slug: string): void {
  storage.set(ULTIMO_PORTAL_KEY, slug);
}

export function olvidarUltimoPortal(): void {
  storage.remove(ULTIMO_PORTAL_KEY);
}

/** La última empresa con la que se abrió sesión, o `null` si no hay ninguna. */
export function ultimoPortalActivo(): string | null {
  return storage.get<string>(ULTIMO_PORTAL_KEY);
}

/**
 * Una aproximación simple a "de qué empresa es esto ahora mismo", sin mirar
 * qué hay guardado: la de la URL si la nombra, y si no, la última con la que
 * se abrió sesión.
 *
 * Es el último recurso de `slugDeLaClave` (cuando no hay NADA guardado en
 * ninguna de las tres claves candidatas) y la empresa bajo la que escriben
 * `impersonate`, `exitImpersonation` y `refreshTokenAction`: en esas tres la
 * clave por-empresa ya existe, así que coincide con lo que resuelve
 * `slugDeLaClave` en las dos formas de URL desde las que se las puede llamar
 * (`/admin/users` y `/datec/admin/users`).
 *
 * 🔴 No sirve para decidir dónde escribir cuando la URL nombra una empresa
 * que no tiene nada guardado (`/quotes`, un bookmark viejo): ahí devuelve
 * `'quotes'` y la lectura resuelve otra cosa. Para eso está `slugDeLaClave`.
 */
export function slugEfectivo(): string | null {
  return slugDeLaUrlActual() ?? ultimoPortalActivo();
}

/**
 * Bajo qué empresa hay que leer, escribir o borrar `base` ahora mismo — la
 * ÚNICA respuesta a esa pregunta en todo el frontend.
 *
 * 🔴 El invariante que sostiene todo esto: **quien escribe un token de sesión
 * escribe la clave que el interceptor de Axios va a leer para la URL en la
 * que está la persona.** Tres rondas de revisión lo rompieron en tres lugares
 * distintos (`switchOrganization`, `impersonate`, `refreshTokenAction`):
 * siempre el mismo error — escribir bajo una empresa y leer bajo otra,
 * mientras la pantalla muestra la primera. Por eso `leerPorPortal` está
 * escrita EN TÉRMINOS de esta función y no al lado: no quedan dos órdenes de
 * resolución que alguien tenga que mantener sincronizados a mano.
 *
 * El orden:
 *
 * 1. La empresa que dice la URL, si tiene algo guardado ahí.
 * 2. La clave global, si tiene algo. La escriben `register` y
 *    `acceptInvitation`, que no pasan por `abrirSesion`; y sólo puede tener
 *    algo si acaban de escribirla, porque `abrirSesion` la borra en cuanto
 *    existe una sesión por-empresa de verdad.
 *
 *    🔴 Va ANTES del puntero del paso 3 a propósito, y esto es lo más
 *    delicado del archivo: al revés, una persona NUEVA que acepta una
 *    invitación en un navegador donde ya entró otra persona aterrizaba en la
 *    empresa de la ANTERIOR, con el token de la anterior — dos personas
 *    distintas, no dos empresas de la misma persona.
 * 3. La última empresa con la que se abrió sesión (`ultimoPortalActivo`).
 *    Es lo que salva a una URL que no nombra ninguna empresa real: la
 *    landing, o `/quotes`, un bookmark de antes del prefijo de empresa.
 *
 * Si ninguno de los tres tiene nada, cae a `slugEfectivo()`: no hay valor que
 * leer, pero igual hay que decidir dónde escribir o qué borrar.
 *
 * Esto NO reabre el aislamiento entre pestañas: una pestaña ya reconciliada
 * en `/datec/...` tiene `auth_token:datec` escrito de verdad (lo escribió su
 * propio canje), así que el paso 1 responde y los otros dos no se evalúan.
 */
export function slugDeLaClave(base: string): string | null {
  const slugUrl = slugDeLaUrlActual();
  if (slugUrl && storage.get(clavePorPortal(base, slugUrl)) !== null) return slugUrl;

  if (storage.get(base) !== null) return null;

  const ultimo = ultimoPortalActivo();
  if (ultimo && storage.get(clavePorPortal(base, ultimo)) !== null) return ultimo;

  return slugEfectivo();
}

/** El valor de `base` para la empresa que resuelve `slugDeLaClave`. */
export function leerPorPortal<T>(base: string): T | null {
  return storage.get<T>(clavePorPortal(base, slugDeLaClave(base)));
}
